// CRM Dashboard — Full wiring to all 9 backend endpoints (pipeline, activities, tasks)
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  EnvelopeIcon,
  FunnelIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import type { PipelineEntry } from './types';
import LeadDetailDrawer from './components/LeadDetailDrawer';

interface PipelineStats {
  summary: { total_leads: number; total_pipeline_value: number; conversion_rate: number };
  [stage: string]:
    | { count: number; avg_probability: number; total_value: number; avg_days_in_stage: number }
    | any;
}

interface CRMActivity {
  id: string;
  activity_type: string;
  title: string;
  description: string;
  activity_date: string | null;
  duration_minutes: number | null;
  outcome: string | null;
  next_action: string | null;
  customer_name: string;
  performed_by: string;
  vehicle_id: string | null;
  created_at: string | null;
}

interface CRMTask {
  id: string;
  title: string;
  description: string;
  task_type: string;
  status: string;
  priority: string;
  due_date: string | null;
  completed_at: string | null;
  customer_name: string | null;
  assigned_to: string;
  created_by: string;
  created_at: string | null;
}

interface CustomerOption {
  id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
}

/* ─── Constants ─── */
const pipelineStages: Record<string, { name: string; color: string }> = {
  new_lead: { name: 'New Lead', color: 'bg-gray-100 text-gray-800' },
  qualified: { name: 'Qualified', color: 'bg-blue-100 text-blue-800' },
  interested: { name: 'Interested', color: 'bg-purple-100 text-purple-800' },
  test_drive: { name: 'Test Drive', color: 'bg-yellow-100 text-yellow-800' },
  negotiating: { name: 'Negotiating', color: 'bg-orange-100 text-orange-800' },
  closed_won: { name: 'Closed Won', color: 'bg-green-100 text-green-800' },
  closed_lost: { name: 'Closed Lost', color: 'bg-red-100 text-red-800' },
};

const activityTypes = [
  'PHONE_CALL',
  'EMAIL',
  'TEXT_MESSAGE',
  'IN_PERSON',
  'TEST_DRIVE',
  'FOLLOW_UP',
  'NOTE',
];
const taskStatuses = ['pending', 'completed', 'overdue', 'cancelled'];
const taskPriorities = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

/* ─── Component ─── */
const CRMDashboard: React.FC = () => {
  const { t } = useTranslation(['crm', 'common']);
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerIdFilter = searchParams.get('customer_id');
  const activeTabParam = searchParams.get('tab') as 'pipeline' | 'activities' | 'tasks' | null;

  const [pipelineData, setPipelineData] = useState<PipelineEntry[]>([]);
  const [stats, setStats] = useState<PipelineStats | null>(null);
  const [activities, setActivities] = useState<CRMActivity[]>([]);
  const [tasks, setTasks] = useState<CRMTask[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pipeline' | 'activities' | 'tasks'>(
    activeTabParam || 'pipeline'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<CRMTask | null>(null);
  const [selectedLead, setSelectedLead] = useState<PipelineEntry | null>(null);

  // Activity form
  const [activityForm, setActivityForm] = useState({
    activity_type: 'PHONE_CALL',
    title: '',
    description: '',
    customer_id: customerIdFilter || '',
    duration_minutes: 0,
    outcome: '',
    next_action: '',
  });

  // Task form
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    task_type: 'FOLLOW_UP',
    priority: 'MEDIUM',
    due_date: '',
    customer_id: '',
    assigned_to_id: '',
  });

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params for filtering by customer if provided
      const activitiesUrl = customerIdFilter
        ? `/crm/activities?limit=50&customer_id=${customerIdFilter}`
        : '/crm/activities?limit=50';
      const tasksUrl = customerIdFilter
        ? `/crm/tasks?limit=50&customer_id=${customerIdFilter}`
        : '/crm/tasks?limit=50';

      const [pipeline, pipelineStats, activityList, taskList] = await Promise.all([
        api.get<PipelineEntry[]>('/crm/pipeline'),
        api.get<PipelineStats>('/crm/pipeline/stats'),
        api.get<CRMActivity[]>(activitiesUrl),
        api.get<CRMTask[]>(tasksUrl),
      ]);
      setPipelineData(pipeline);
      setStats(pipelineStats);
      setActivities(activityList);
      setTasks(taskList);
    } catch (err) {
      console.error('Error loading CRM data:', err);
      setError(t('errors.dataLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [customerIdFilter, t]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Lazy-load customers the first time the Log Activity modal opens.
  // Kept out of loadAllData so a /customers failure can't break the
  // pipeline/activities/tasks view, and so the n=1000 fetch doesn't
  // run on every CRM page mount.
  const [customersLoaded, setCustomersLoaded] = useState(false);
  useEffect(() => {
    if (!showActivityModal || customersLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await api.get<CustomerOption[]>('/customers/?limit=1000');
        if (cancelled) return;
        setCustomers(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) {
          setCustomers([]);
          toast.error(t('errors.customersLoadFailed'));
        }
      } finally {
        if (!cancelled) setCustomersLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showActivityModal, customersLoaded, t]);

  /* ─── Pipeline stats from /pipeline/stats ─── */
  const stageStats = Object.keys(pipelineStages).reduce(
    (acc, stage) => {
      acc[stage] = pipelineData.filter((e) => e.stage === stage).length;
      return acc;
    },
    {} as Record<string, number>
  );

  const totalLeads = stats?.summary?.total_leads ?? pipelineData.length;
  const totalPipelineValue =
    stats?.summary?.total_pipeline_value ?? pipelineData.reduce((s, e) => s + e.estimated_value, 0);
  const avgDealSize = totalLeads > 0 ? totalPipelineValue / totalLeads : 0;
  const conversionRate = stats?.summary?.conversion_rate ?? 0;

  /* ─── Helpers ─── */
  const getDaysInStage = (d: string) =>
    Math.ceil(Math.abs(Date.now() - new Date(d).getTime()) / 86400000);
  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  const fmtDate = (s: string) =>
    new Date(s).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  const getStageLeads = (stage: string) => pipelineData.filter((e) => e.stage === stage);
  const getStageLabel = (stage: string) =>
    t(`pipeline.stages.${stage}`, { defaultValue: pipelineStages[stage]?.name || stage });
  const getActivityTypeLabel = (type: string) =>
    t(`activityTypes.${type}`, { defaultValue: type.replace(/_/g, ' ') });

  /* ─── Activity CRUD ─── */
  const handleCreateActivity = async () => {
    if (!activityForm.customer_id) {
      toast.error(t('common:messages.required', { field: t('common:fields.customer') }));
      return;
    }
    if (!activityForm.title.trim()) {
      toast.error(t('common:messages.required', { field: t('common:fields.title') }));
      return;
    }
    try {
      await api.post('/crm/activities', {
        ...activityForm,
        duration_minutes: activityForm.duration_minutes || undefined,
      });
      toast.success(t('workflow.logActivity.logged'));
      setShowActivityModal(false);
      setActivityForm({
        activity_type: 'PHONE_CALL',
        title: '',
        description: '',
        customer_id: customerIdFilter || '',
        duration_minutes: 0,
        outcome: '',
        next_action: '',
      });
      loadAllData();
    } catch {
      toast.error(t('workflow.logActivity.createFailed'));
    }
  };

  /* ─── Task CRUD ─── */
  const handleCreateTask = async () => {
    if (!taskForm.title.trim()) {
      toast.error(t('common:messages.required', { field: t('common:fields.title') }));
      return;
    }
    try {
      await api.post('/crm/tasks', {
        ...taskForm,
        customer_id: taskForm.customer_id || undefined,
        assigned_to_id: taskForm.assigned_to_id || currentUser?.id,
        due_date: taskForm.due_date || undefined,
      });
      toast.success(t('tasks.created'));
      setShowTaskModal(false);
      setTaskForm({
        title: '',
        description: '',
        task_type: 'FOLLOW_UP',
        priority: 'MEDIUM',
        due_date: '',
        customer_id: '',
        assigned_to_id: '',
      });
      loadAllData();
    } catch {
      toast.error(t('tasks.createFailed'));
    }
  };

  const handleUpdateTask = async (taskId: string, updates: Record<string, unknown>) => {
    try {
      await api.put(`/crm/tasks/${taskId}`, updates);
      toast.success(t('tasks.updated'));
      setShowEditTaskModal(false);
      setEditingTask(null);
      loadAllData();
    } catch {
      toast.error(t('tasks.updateFailed'));
    }
  };

  const handleCompleteTask = (taskId: string) => handleUpdateTask(taskId, { status: 'COMPLETED' });

  /* ─── Pipeline stage update ─── */
  const handleUpdatePipelineStage = async (pipelineId: string, newStage: string) => {
    try {
      await api.put(`/crm/pipeline/${pipelineId}`, { stage: newStage });
      toast.success(t('pipeline.movedTo', { stage: getStageLabel(newStage) }));
      loadAllData();
    } catch {
      toast.error(t('errors.pipelineStageUpdateFailed'));
    }
  };

  /* ─── Sub-components ─── */
  const LeadCard: React.FC<{ lead: PipelineEntry }> = ({ lead }) => {
    const currentIdx = Object.keys(pipelineStages).indexOf(lead.stage);
    const nextStage = Object.keys(pipelineStages)[currentIdx + 1];
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => setSelectedLead(lead)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') setSelectedLead(lead);
        }}
        className="bg-white rounded-lg border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-semibold text-gray-900">
              {lead.customer.first_name} {lead.customer.last_name}
            </h4>
            <div className="flex items-center space-x-4 mt-1">
              <span className="flex items-center text-sm text-gray-500">
                <PhoneIcon className="h-4 w-4 mr-1" />
                {lead.customer.phone}
              </span>
              <span className="flex items-center text-sm text-gray-500">
                <EnvelopeIcon className="h-4 w-4 mr-1" />
                {lead.customer.email}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-green-600">{fmt(lead.estimated_value)}</div>
            <div className="text-sm text-gray-500">{lead.probability}% probability</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center text-gray-500">
            <ClockIcon className="h-4 w-4 mr-1" />
            {t('common.days', { count: getDaysInStage(lead.stage_entered_at) })}
          </span>
          <span className="text-gray-600">{lead.lead_source || t('common.noSource')}</span>
        </div>
        <div className="mt-2 text-xs text-gray-400">
          {t('pipeline.lastActivity', {
            value: lead.last_activity_date ? fmtDate(lead.last_activity_date) : t('common.none'),
          })}
        </div>
        <div className="mt-2 flex justify-between items-center text-xs text-gray-500">
          <div>
            <span>{t('pipeline.activitiesCount', { count: lead.activity_count })}</span>
            <span className="ml-3">{t('pipeline.tasksCount', { count: lead.pending_tasks })}</span>
          </div>
          {nextStage && nextStage !== 'closed_lost' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleUpdatePipelineStage(lead.id, nextStage);
              }}
              className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
            >
              {t('pipeline.moveTo', { stage: getStageLabel(nextStage) })}
              <ChevronRightIcon className="h-3 w-3 ml-1" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
    const c: Record<string, string> = {
      URGENT: 'bg-red-100 text-red-800',
      HIGH: 'bg-orange-100 text-orange-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      LOW: 'bg-gray-100 text-gray-600',
    };
    return (
      <span
        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${c[priority] || c.MEDIUM}`}
      >
        {t(`tasks.priority.${priority}`)}
      </span>
    );
  };

  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const c: Record<string, string> = {
      completed: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-gray-100 text-gray-600',
    };
    return (
      <span
        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${c[status] || c.pending}`}
      >
        {t(`tasks.status.${status}`, { defaultValue: status.replace('_', ' ') })}
      </span>
    );
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common:messages.loading')}</div>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 mb-2">{error}</div>
          <button onClick={loadAllData} className="btn-primary">
            {t('common:actions.retry')}
          </button>
        </div>
      </div>
    );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('header.title')}</h1>
          <p className="text-gray-600">{t('header.subtitle')}</p>
        </div>
        <div className="flex space-x-2">
          <button onClick={() => setShowActivityModal(true)} className="btn-secondary">
            <PlusIcon className="h-4 w-4 mr-1" /> {t('workflow.logActivity.title')}
          </button>
          <button onClick={() => setShowTaskModal(true)} className="btn-primary">
            <PlusIcon className="h-4 w-4 mr-1" /> {t('tasks.newTask')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">{t('pipeline.stats.totalLeads')}</p>
              <p className="text-2xl font-bold text-gray-900">{totalLeads}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <FunnelIcon className="h-8 w-8 text-green-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">
                {t('pipeline.stats.pipelineValue')}
              </p>
              <p className="text-2xl font-bold text-gray-900">{fmt(totalPipelineValue)}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <CalendarDaysIcon className="h-8 w-8 text-purple-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">{t('pipeline.stats.avgDealSize')}</p>
              <p className="text-2xl font-bold text-gray-900">{fmt(avgDealSize)}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <ChevronRightIcon className="h-8 w-8 text-orange-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">
                {t('pipeline.stats.conversionRate')}
              </p>
              <p className="text-2xl font-bold text-gray-900">{conversionRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {(['pipeline', 'activities', 'tasks'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              {t(`tabs.${tab}`)}
              {tab === 'tasks' && tasks.filter((t) => t.status === 'pending').length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {tasks.filter((t) => t.status === 'pending').length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ─── Pipeline Tab ─── */}
      {activeTab === 'pipeline' && (
        <>
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">{t('pipeline.salesPipeline')}</h3>
              <p className="text-sm text-gray-500">{t('pipeline.stageInstruction')}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {Object.entries(pipelineStages).map(([key, stage]) => {
                const count = stageStats[key] || 0;
                const pct = totalLeads > 0 ? ((count / totalLeads) * 100).toFixed(1) : '0.0';
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedStage(selectedStage === key ? null : key)}
                    className={`p-4 rounded-lg border-2 transition-all ${selectedStage === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{count}</div>
                      <div className="text-sm font-medium text-gray-700 mt-1">
                        {getStageLabel(key)}
                      </div>
                      <div
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${stage.color}`}
                      >
                        {pct}%
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedStage && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">
                  {getStageLabel(selectedStage)}
                </h3>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${pipelineStages[selectedStage]?.color}`}
                >
                  {t('pipeline.leadCount', { count: getStageLeads(selectedStage).length })}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getStageLeads(selectedStage).map((lead) => (
                  <LeadCard key={lead.id} lead={lead} />
                ))}
              </div>
              {getStageLeads(selectedStage).length === 0 && (
                <div className="text-center py-12">
                  <FunnelIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t('pipeline.noLeadsInStage')}
                  </h3>
                  <p className="text-gray-500">{t('pipeline.noLeadsInStageDescription')}</p>
                </div>
              )}
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">{t('pipeline.recentActivity')}</h3>
            </div>
            <div className="space-y-4">
              {pipelineData.slice(0, 5).map((lead) => (
                <div
                  key={lead.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedLead(lead)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setSelectedLead(lead);
                  }}
                  className="flex cursor-pointer items-center justify-between rounded-md py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-3">
                    <UserGroupIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        {lead.customer.first_name} {lead.customer.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{lead.customer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pipelineStages[lead.stage]?.color || 'bg-gray-100 text-gray-800'}`}
                    >
                      {getStageLabel(lead.stage)}
                    </span>
                    <span className="text-sm font-medium text-green-600">
                      {fmt(lead.estimated_value)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {getDaysInStage(lead.stage_entered_at)}d
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {pipelineData.length === 0 && (
              <div className="text-center py-12">
                <FunnelIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('pipeline.emptyState.title')}
                </h3>
                <p className="text-gray-500">{t('pipeline.emptyState.description')}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* ─── Activities Tab ─── */}
      {activeTab === 'activities' && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">{t('activities.title')}</h3>
            <button onClick={() => setShowActivityModal(true)} className="btn-secondary text-sm">
              <PlusIcon className="h-4 w-4 mr-1" /> {t('workflow.logActivity.title')}
            </button>
          </div>
          {activities.length === 0 ? (
            <div className="text-center py-12">
              <CalendarDaysIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('activities.emptyState.title')}
              </h3>
              <p className="text-gray-500">{t('activities.emptyState.description')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.type')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.title')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.customer')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('activities.table.by')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.outcome')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.date')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {activities.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {getActivityTypeLabel(a.activity_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{a.title}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{a.customer_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{a.performed_by}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{a.outcome || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {a.activity_date ? fmtDate(a.activity_date) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── Tasks Tab ─── */}
      {activeTab === 'tasks' && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">{t('tasks.title')}</h3>
            <button onClick={() => setShowTaskModal(true)} className="btn-secondary text-sm">
              <PlusIcon className="h-4 w-4 mr-1" /> {t('tasks.newTask')}
            </button>
          </div>
          {tasks.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('tasks.emptyState.title')}
              </h3>
              <p className="text-gray-500">{t('tasks.emptyState.description')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.title')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.customer')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.priority')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.status')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.due')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.assigned')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      {t('common:fields.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tasks.map((task) => (
                    <tr
                      key={task.id}
                      className={`hover:bg-gray-50 ${task.status === 'COMPLETED' ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{task.title}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {task.customer_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <PriorityBadge priority={task.priority} />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <StatusBadge status={task.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {task.due_date ? new Date(task.due_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{task.assigned_to}</td>
                      <td className="px-4 py-3 text-sm text-right space-x-2">
                        {task.status !== 'COMPLETED' && (
                          <>
                            <button
                              onClick={() => handleCompleteTask(task.id)}
                              className="text-green-600 hover:text-green-800"
                              title={t('tasks.actions.complete')}
                            >
                              <CheckCircleIcon className="h-4 w-4 inline" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingTask(task);
                                setShowEditTaskModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                              title={t('tasks.actions.edit')}
                            >
                              <PencilIcon className="h-4 w-4 inline" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── Log Activity Modal ─── */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('workflow.logActivity.title')}
              </h3>
              <button
                onClick={() => setShowActivityModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.logActivity.customerLabel')}
                </label>
                <select
                  value={activityForm.customer_id}
                  onChange={(e) =>
                    setActivityForm({ ...activityForm, customer_id: e.target.value })
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  aria-label={t('workflow.logActivity.customerAriaLabel')}
                >
                  <option value="">{t('workflow.logActivity.selectCustomer')}</option>
                  {customers.map((c) => {
                    const label =
                      `${c.first_name || ''} ${c.last_name || ''}`.trim() ||
                      t('workflow.logActivity.unnamedCustomer');
                    const detail = c.phone || c.email || '';
                    return (
                      <option key={c.id} value={c.id}>
                        {detail ? `${label} — ${detail}` : label}
                      </option>
                    );
                  })}
                </select>
                {customers.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    {t('workflow.logActivity.noCustomers')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.logActivity.activityType')}
                </label>
                <select
                  value={activityForm.activity_type}
                  onChange={(e) =>
                    setActivityForm({ ...activityForm, activity_type: e.target.value })
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {activityTypes.map((t) => (
                    <option key={t} value={t}>
                      {getActivityTypeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.logActivity.titleLabel')}
                </label>
                <input
                  type="text"
                  value={activityForm.title}
                  onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder={t('workflow.logActivity.placeholders.title')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.logActivity.descriptionLabel')}
                </label>
                <textarea
                  value={activityForm.description}
                  onChange={(e) =>
                    setActivityForm({ ...activityForm, description: e.target.value })
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  rows={3}
                  placeholder={t('workflow.logActivity.placeholders.description')}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workflow.logActivity.duration')}
                  </label>
                  <input
                    type="number"
                    value={activityForm.duration_minutes}
                    onChange={(e) =>
                      setActivityForm({
                        ...activityForm,
                        duration_minutes: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workflow.logActivity.outcome')}
                  </label>
                  <input
                    type="text"
                    value={activityForm.outcome}
                    onChange={(e) => setActivityForm({ ...activityForm, outcome: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    placeholder={t('workflow.logActivity.placeholders.outcome')}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.logActivity.nextAction')}
                </label>
                <input
                  type="text"
                  value={activityForm.next_action}
                  onChange={(e) =>
                    setActivityForm({ ...activityForm, next_action: e.target.value })
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder={t('workflow.logActivity.placeholders.nextAction')}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => setShowActivityModal(false)} className="btn-secondary">
                {t('common:actions.cancel')}
              </button>
              <button onClick={handleCreateActivity} className="btn-primary">
                {t('workflow.logActivity.title')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── New Task Modal ─── */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('tasks.createTitle')}</h3>
              <button
                onClick={() => setShowTaskModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common:fields.title')}
                </label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder={t('tasks.placeholders.title')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common:fields.description')}
                </label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common:fields.priority')}
                  </label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    {taskPriorities.map((p) => (
                      <option key={p} value={p}>
                        {t(`tasks.priority.${p}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common:fields.dueDate')}
                  </label>
                  <input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => setShowTaskModal(false)} className="btn-secondary">
                {t('common:actions.cancel')}
              </button>
              <button onClick={handleCreateTask} className="btn-primary">
                {t('tasks.createTitle')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Edit Task Modal ─── */}
      {showEditTaskModal && editingTask && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('tasks.editTitle')}</h3>
              <button
                onClick={() => {
                  setShowEditTaskModal(false);
                  setEditingTask(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common:fields.status')}
                </label>
                <select
                  value={editingTask.status}
                  onChange={(e) => setEditingTask({ ...editingTask, status: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {taskStatuses.map((s) => (
                    <option key={s} value={s}>
                      {t(`tasks.status.${s}`, { defaultValue: s.replace(/_/g, ' ') })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common:fields.priority')}
                </label>
                <select
                  value={editingTask.priority}
                  onChange={(e) => setEditingTask({ ...editingTask, priority: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {taskPriorities.map((p) => (
                    <option key={p} value={p}>
                      {t(`tasks.priority.${p}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditTaskModal(false);
                  setEditingTask(null);
                }}
                className="btn-secondary"
              >
                {t('common:actions.cancel')}
              </button>
              <button
                onClick={() =>
                  handleUpdateTask(editingTask.id, {
                    status: editingTask.status,
                    priority: editingTask.priority,
                  })
                }
                className="btn-primary"
              >
                {t('common:actions.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      <LeadDetailDrawer
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onViewCustomer={(customerId) => {
          // Land on the customer's Activities tab — the Pipeline tab is
          // customer-agnostic, so filtering by customer_id there shows nothing.
          setSearchParams({ customer_id: customerId, tab: 'activities' });
          setActiveTab('activities');
          setSelectedLead(null);
        }}
      />
    </div>
  );
};

export default CRMDashboard;
